import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Copy, MessageCircle, Share2, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { API, inr, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const REF_COLOR = { Clicked: "bg-stone-100 text-stone-600", "Enquiry Submitted": "bg-blue-50 text-blue-700", Contacted: "bg-purple-50 text-purple-700", Qualified: "bg-indigo-50 text-indigo-700", Discussion: "bg-amber-50 text-amber-700", "Outlet Approved": "bg-teal-50 text-teal-700", "Payment Received": "bg-green-50 text-green-700", "Commission Eligible": "bg-green-100 text-green-800", "Commission Processing": "bg-amber-100 text-amber-800", "Commission Paid": "bg-green-600 text-white", Rejected: "bg-red-50 text-red-600" };
const COM_COLOR = { Pending: "bg-amber-50 text-amber-700", Approved: "bg-green-50 text-green-700", Processing: "bg-blue-50 text-blue-700", Paid: "bg-green-600 text-white", Cancelled: "bg-red-50 text-red-600" };

export default function PartnerHub() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [tab, setTab] = useState("overview");
  const [amount, setAmount] = useState("");

  const load = useCallback(() => API.get("/affiliate/me").then((r) => setDash(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); API.get("/affiliate/materials").then((r) => setMaterials(r.data)).catch(() => {}); }, [load]);

  if (!dash) return <div className="max-w-7xl mx-auto px-4 py-20 text-stone-500">Loading Partner Hub…</div>;

  const { affiliate, stats, wallet, referrals, commissions, payouts, settings } = dash;
  const refLink = `${window.location.origin}/open-outlet?ref=${affiliate.affiliate_id}`;
  const shareText = encodeURIComponent(`Open your own SUVIKKA women's lifestyle retail outlet — ₹3,00,000 retail outlet opportunity. Enquire here: ${refLink}`);

  const copy = () => { navigator.clipboard.writeText(refLink); toast.success("Referral link copied"); };

  const requestPayout = async () => {
    try {
      await API.post("/affiliate/payout", { amount: Number(amount || wallet.withdrawable) });
      toast.success("Payout requested. Admin will process it.");
      setAmount(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const chartData = [
    { name: "Clicks", value: stats.clicks }, { name: "Enquiries", value: stats.total_referrals },
    { name: "Qualified", value: stats.qualified + stats.in_progress }, { name: "Conversions", value: stats.conversions },
  ];

  const TABS = [["overview", "Overview"], ["referrals", "My Referrals"], ["wallet", "Commission Wallet"], ["performance", "Performance"], ["promote", "Promote Suvikka"]];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="partner-hub">
      <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29]">Affiliate Dashboard</p>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900 mt-2">SUVIKKA PARTNER HUB</h1>
      <p className="text-stone-500 mt-1">Welcome, {user?.name} · Affiliate ID: <strong className="text-stone-900" data-testid="ph-affiliate-id">{affiliate.affiliate_id}</strong>
        <span className={`ml-2 text-xs font-semibold px-2.5 py-1 rounded-full ${affiliate.status === "active" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`} data-testid="ph-status">{affiliate.status}</span></p>

      <div className="mt-8 bg-stone-900 rounded-2xl p-6 sm:p-8 text-white" data-testid="ph-referral-link-box">
        <p className="text-xs tracking-[0.25em] uppercase text-stone-400">Your Unique Referral Link</p>
        <div className="flex flex-col sm:flex-row gap-3 mt-3">
          <input readOnly value={refLink} className="flex-1 bg-white/10 border border-white/15 rounded-lg px-4 py-3 text-sm text-stone-200 outline-none" data-testid="ph-ref-link" />
          <div className="flex gap-2">
            <button onClick={copy} data-testid="ph-copy-link" className="inline-flex items-center gap-1.5 bg-[#6A1B29] hover:bg-[#501325] text-white text-sm font-semibold px-4 py-3 rounded-lg"><Copy className="w-4 h-4" /> COPY LINK</button>
            <a href={`https://wa.me/?text=${shareText}`} target="_blank" rel="noreferrer" data-testid="ph-share-whatsapp" className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-3 rounded-lg"><MessageCircle className="w-4 h-4" /> WHATSAPP</a>
            <button onClick={() => navigator.share ? navigator.share({ title: "Suvikka Outlet Opportunity", url: refLink }) : copy()} data-testid="ph-share-button" className="inline-flex items-center gap-1.5 border border-white/25 text-white text-sm font-semibold px-4 py-3 rounded-lg"><Share2 className="w-4 h-4" /> SHARE</button>
          </div>
        </div>
      </div>

      <div className="flex gap-2 mt-8 overflow-x-auto pb-1" data-testid="partner-hub-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ph-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${tab === k ? "bg-stone-900 text-white" : "bg-white border border-stone-300 text-stone-700"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "overview" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[["Total Referrals", stats.total_referrals], ["New Leads", stats.new_leads], ["Contacted", stats.contacted], ["In Discussion", stats.in_progress], ["Successful Conversions", stats.conversions],
              ["Eligible Commission", inr(wallet.total_earned)], ["Commission Pending", inr(wallet.pending)], ["Commission Paid", inr(wallet.paid)], ["Withdrawable", inr(wallet.withdrawable)], ["Total Clicks", stats.clicks]].map(([l, v], i) => (
              <div key={i} className="bg-white border border-stone-200 rounded-xl p-4 sm:p-5" data-testid={`ph-stat-${i}`}>
                <p className="text-[11px] text-stone-400 uppercase tracking-wider">{l}</p>
                <p className="font-display text-xl sm:text-2xl font-bold text-stone-900 mt-1">{v}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "referrals" && (
          <div className="bg-white border border-stone-200 rounded-2xl overflow-x-auto" data-testid="ph-referrals-table">
            <table className="w-full text-sm min-w-[640px]">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-stone-200">
                <th className="p-4">Referral</th><th className="p-4">City / State</th><th className="p-4">Lead ID</th><th className="p-4">Date</th><th className="p-4">Status</th></tr></thead>
              <tbody>
                {referrals.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-stone-500" data-testid="ph-referrals-empty">No referrals yet. Share your link to get started.</td></tr>}
                {referrals.map((r) => (
                  <tr key={r.id} className="border-b border-stone-100">
                    <td className="p-4 font-medium">{r.name}</td>
                    <td className="p-4 text-stone-500">{r.city}{r.state ? `, ${r.state}` : ""}</td>
                    <td className="p-4 text-stone-500">{r.lead_id}</td>
                    <td className="p-4 text-stone-500">{new Date(r.created_at).toLocaleDateString("en-IN")}</td>
                    <td className="p-4"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${REF_COLOR[r.status] || "bg-stone-100"}`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "wallet" && (
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {[["Total Earned", wallet.total_earned], ["Pending", wallet.pending], ["Approved", wallet.approved], ["Paid", wallet.paid], ["Withdrawable", wallet.withdrawable]].map(([l, v], i) => (
                <div key={i} className={`rounded-xl p-4 sm:p-5 border ${i === 4 ? "bg-[#FFF0F4] border-[#F8C4D0]" : "bg-white border-stone-200"}`} data-testid={`wallet-${i}`}>
                  <p className="text-[11px] text-stone-400 uppercase tracking-wider">{l}</p>
                  <p className="font-display text-xl sm:text-2xl font-bold text-stone-900 mt-1">{inr(v)}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-white border border-stone-200 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="payout-request-box">
              <div className="flex-1">
                <p className="text-xs text-stone-400 uppercase tracking-wider">Available Commission</p>
                <p className="font-display text-2xl font-bold text-stone-900" data-testid="available-commission">{inr(wallet.withdrawable)}</p>
                <p className="text-xs text-stone-500 mt-1">Minimum payout {inr(settings.min_payout)} · Payout schedule: {settings.payout_schedule}. Payouts are approved and processed by the Suvikka team.</p>
              </div>
              <div className="flex gap-2">
                <input type="number" placeholder={String(wallet.withdrawable)} value={amount} onChange={(e) => setAmount(e.target.value)}
                  className="w-32 border border-stone-300 rounded-lg px-3 py-2.5 text-sm outline-none" data-testid="payout-amount" />
                <button onClick={requestPayout} disabled={wallet.withdrawable < settings.min_payout} data-testid="request-payout-button"
                  className="bg-[#6A1B29] hover:bg-[#501325] disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-lg">REQUEST PAYOUT</button>
              </div>
            </div>
            <div className="mt-6 bg-white border border-stone-200 rounded-2xl overflow-x-auto" data-testid="commission-history">
              <table className="w-full text-sm min-w-[720px]">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-stone-200">
                  <th className="p-4">Date</th><th className="p-4">Referral</th><th className="p-4">Outlet Status</th><th className="p-4">Eligible Amount</th><th className="p-4">Commission</th><th className="p-4">Status</th></tr></thead>
                <tbody>
                  {commissions.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-stone-500">No commissions yet.</td></tr>}
                  {commissions.map((c) => (
                    <tr key={c.id} className="border-b border-stone-100">
                      <td className="p-4 text-stone-500">{new Date(c.created_at).toLocaleDateString("en-IN")}</td>
                      <td className="p-4 font-medium">{c.referral_name}</td>
                      <td className="p-4 text-stone-500">Payment Received · {c.city}</td>
                      <td className="p-4">{inr(c.eligible_amount)}</td>
                      <td className="p-4 font-semibold">{inr(c.commission)}</td>
                      <td className="p-4"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${COM_COLOR[c.status]}`}>{c.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {payouts.length > 0 && (
              <div className="mt-6 bg-white border border-stone-200 rounded-2xl p-6">
                <h3 className="font-display text-lg font-semibold mb-3">Payout Requests</h3>
                {payouts.map((p) => (
                  <div key={p.id} className="flex justify-between items-center border-b border-stone-100 py-3 text-sm" data-testid={`payout-${p.id.slice(0, 6)}`}>
                    <span>{new Date(p.created_at).toLocaleDateString("en-IN")} · {inr(p.amount)}</span>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${p.status === "Paid" ? "bg-green-600 text-white" : p.status === "Rejected" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"}`}>{p.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "performance" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="ph-performance">
            <div className="lg:col-span-2 bg-white border border-stone-200 rounded-2xl p-6">
              <h3 className="font-display text-lg font-semibold mb-4">Funnel Performance</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}><XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} /><Tooltip />
                  <Bar dataKey="value" fill="#6A1B29" radius={[6, 6, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-4">
              {[["Conversion Rate", `${stats.conversion_rate}%`], ["Total Commission", inr(wallet.total_earned)], ["Pending Commission", inr(wallet.pending)], ["Paid Commission", inr(wallet.paid)]].map(([l, v]) => (
                <div key={l} className="bg-white border border-stone-200 rounded-xl p-5">
                  <p className="text-[11px] text-stone-400 uppercase tracking-wider">{l}</p>
                  <p className="font-display text-2xl font-bold text-stone-900 mt-1">{v}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "promote" && (
          <div data-testid="ph-promote">
            <p className="text-sm text-stone-500 mb-5">Ready-to-use creatives. Every creative carries the ₹3,00,000 retail outlet opportunity — Refer. Connect. Earn {settings.commission_percent}% Commission.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {materials.map((m) => (
                <div key={m.id} className="bg-white border border-stone-200 overflow-hidden" data-testid={`material-${m.id.slice(0, 6)}`}>
                  <div className="aspect-square bg-stone-100 overflow-hidden"><img src={m.image} alt={m.title} className="w-full h-full object-cover" /></div>
                  <div className="p-3">
                    <p className="text-xs font-semibold text-[#6A1B29] uppercase tracking-wider">{m.type}</p>
                    <p className="text-sm font-medium mt-0.5">{m.title}</p>
                    <p className="text-[11px] text-stone-500 mt-1 line-clamp-2">{m.caption}</p>
                    <a href={m.image} target="_blank" rel="noreferrer" download data-testid={`material-download-${m.id.slice(0, 6)}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-stone-900 mt-2 hover:text-[#6A1B29]"><Download className="w-3 h-3" /> Download</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
